import Vue from 'vue';
import App from './App.vue';
import router from './router/index';
import { store } from './store/store';
import public_routes from '@/router/public';

import { BootstrapVue, ModalPlugin, BootstrapVueIcons } from 'bootstrap-vue';
import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue/dist/bootstrap-vue.css'
import 'material-design-icons-iconfont/dist/material-design-icons.css'

// Importar métodos y filtros
import {
    getVisibleNames, filtered, equals, hasPermissions, hasObjectPermission,
    lightOrDark, htmlToPlainText, logOut, round, truncateToDecimals
} from '@/utils/utils';
import '@/utils/filters';

Vue.use(BootstrapVue);
Vue.use(ModalPlugin);
Vue.use(BootstrapVueIcons);

import Vuelidate from 'vuelidate';
Vue.use(Vuelidate);

import i18n from './locales/index';

import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
Vue.use(VueSweetalert2);

let moment = require('moment-timezone');
moment.tz.setDefault('America/Santiago');
require('moment/locale/es')
Vue.use(require('vue-moment'), {
    moment
});

import JsonExcel from "vue-json-excel";
Vue.component("downloadExcel", JsonExcel);

import ButtonAdd from '@/components/reusable/ButtonAdd';
Vue.component("button-add", ButtonAdd);

import ButtonEdit from '@/components/reusable/ButtonEdit';
Vue.component("button-edit", ButtonEdit);

import ButtonDelete from '@/components/reusable/ButtonDelete';
Vue.component("button-delete", ButtonDelete);

import Toolbar from '@/components/public/Toolbar.vue';
Vue.component('toolbar-component', Toolbar);

import VueGlobalVar from 'vue-global-var';
import restful from '@/utils/restful';

// Silent update: detección de builds nuevas + guardas de recarga
// (ver ../infra/docs/TODOs/260721-silent_frontend_updates.md)
import { updatePending, remoteVersion, startVersionCheck } from '@/composables/useVersionCheck';
import { recordAttemptedRoute, getAttemptedRoute, isChunkLoadError, oneShotReload } from '@/utils/appUpdate';
import UpdateBanner from '@/components/reusable/UpdateBanner.vue';
Vue.component('update-banner', UpdateBanner);

// Rutas que reciben params que NO están en el path (se pasan en memoria via
// router.push): una carga completa del documento los perdería, así que estas
// navegaciones siguen siendo SPA y quedan cubiertas por las redes de
// recuperación de chunks. (EvaluateeView ← instrument_id desde
// NextTestStudent; EvaluationView ← question_test_id/observation_instrument_id
// desde EvaluationTable.)
const UPDATE_SWAP_EXEMPT = ['EvaluateeView', 'EvaluationView'];

// Variable para controlar si se quiere colocar en mantenimiento las instituciones…
export const maintenance_view = false;
// Variable para controlar el tamaño máximo permitido para los archivos y el peso que se mostrará en sus respectivos mensajes.
export const max_size_in_files = {
    max_size: 12582912,
    max_size_text: "6MB",
}
// (TEMPORAL) Variable para controlar si se muestra una alerta de mantenimiento en el NavBar y su mensaje.
export const maintenance_alert = {
    show: false,
    message: "Sábado 21 de Marzo del 2026 se realizará mantenimiento a la página, por lo que los servicios no estarán disponibles.",
}
// Instituciones (internal_use_id) que usan créditos institucionales y la fórmula con que se calculan.
// Todas ven los créditos institucionales en las mismas vistas; solo cambia el cálculo automático.
const institutional_credits_formulas = {
    "duoc.cl": "duoc",
    "unab.cl": "unab",
    // PUCV: el crédito se ingresa a mano y de él salen los SCT (src/utils/pucvCredits.js).
    "pucv.cl": "pucv",
}
function getInstitutionalCreditsFormula(institution) {
    if (!institution) return null;
    return institutional_credits_formulas[institution.internal_use_id] || null;
}
function hasInstitutionalCredits(institution) {
    return getInstitutionalCreditsFormula(institution) != null;
}
// Instituciones (internal_use_id) que no usan Contenidos por RA Micro: se ocultan en el
// programa, en la planificación de sesiones y en el constructor curricular con IA.
// Debe coincidir con _NO_CONTENTS_INSTITUTIONS en kl-backend (common/ai_builders/curricular.py).
const no_study_unit_contents_institutions = ["pucv.cl"];
function hidesStudyUnitContents(institution) {
    return !!institution && no_study_unit_contents_institutions.includes(institution.internal_use_id);
}
// Instituciones (internal_use_id) que registran el N° de estudiantes por División de tiempo
// (students_per_hour) en Asignaturas, Asignaturas de Perfil de Egreso y el programa.
const students_count_institutions = ["pucv.cl"];
function showsStudentsCount(institution) {
    return !!institution && students_count_institutions.includes(institution.internal_use_id);
}
// Instituciones (internal_use_id) que referencian cada RA Micro a una competencia de la
// asignatura, por perfil de egreso (StudyUnit.ref_epr_competences).
const study_unit_ref_competence_institutions = ["pucv.cl"];
function showsStudyUnitRefCompetence(institution) {
    return !!institution && study_unit_ref_competence_institutions.includes(institution.internal_use_id);
}
// Instituciones (internal_use_id) que no muestran la columna de Horas de Trabajo Autónomo
// en la tabla de Actividades y Evaluaciones del programa.
const no_autonomous_work_hours_institutions = ["pucv.cl"];
function hidesAutonomousWorkHours(institution) {
    return !!institution && no_autonomous_work_hours_institutions.includes(institution.internal_use_id);
}
// Instituciones (internal_use_id) que en la presentación del programa solo muestran las
// Divisiones de tiempo con horas > 0 con su exigencia.
const non_zero_time_allocations_institutions = ["udalba.cl"];
function showsOnlyNonZeroTimeAllocations(institution) {
    return !!institution && non_zero_time_allocations_institutions.includes(institution.internal_use_id);
}
// Instituciones (internal_use_id) que en la presentación del programa solo muestran las
// Divisiones de tiempo con algún dato: horas, exigencia o N° de estudiantes > 0.
// "Cuenta para créditos" no se considera: viene activo por defecto y no ocultaría nada.
const filled_time_allocations_institutions = ["pucv.cl"];
function showsOnlyFilledTimeAllocations(institution) {
    return !!institution && filled_time_allocations_institutions.includes(institution.internal_use_id);
}
// Instituciones (internal_use_id) cuyo Tipo de Asignatura varía según el plan de estudio:
// siempre se edita y muestra el de la Asignatura del Perfil de Egreso, aunque use los
// valores de la asignatura base (use_matter_values).
const egress_profile_matter_types_institutions = ["pucv.cl"];
function usesEgressProfileMatterTypes(institution) {
    return !!institution && egress_profile_matter_types_institutions.includes(institution.internal_use_id);
}
// Instituciones (internal_use_id) que no usan Áreas de Formación: se ocultan en el
// formulario de Asignatura y en la presentación del programa (la malla ya las omite si
// la asignatura no tiene ninguna).
const no_formation_area_institutions = ["pucv.cl"];
function hidesFormationArea(institution) {
    return !!institution && no_formation_area_institutions.includes(institution.internal_use_id);
}

// Instituciones (internal_use_id) que usan Ámbitos de realización: listado propio de cada perfil
// de egreso que se vincula a sus competencias y, en la Matriz 2, a sus asignaturas (como las
// Capacidades de Duoc, pero por perfil en vez de institucionales o por facultad).
const realization_scopes_institutions = ["uniacc.cl"];
function usesRealizationScopes(institution) {
    return !!institution && realization_scopes_institutions.includes(institution.internal_use_id);
}

// Instituciones (internal_use_id) con la vista Carga Académica (formato Anexo 4 PUCV) y con
// Tipo de sala y Periodicidad por División de tiempo, junto al N° de estudiantes.
const academic_load_institutions = ["pucv.cl"];
function usesAcademicLoad(institution) {
    return !!institution && academic_load_institutions.includes(institution.internal_use_id);
}

Vue.use(VueGlobalVar, {
    globals: {
        $restful: restful,
        $getVisibleNames: getVisibleNames,
        $filtered: filtered,
        $equals: equals,
        $truncateToDecimals: truncateToDecimals,
        $round: round,
        $hasPermissions: hasPermissions,
        $hasObjectPermission: hasObjectPermission,
        $lightOrDark: lightOrDark,
        $htmlToPlainText: htmlToPlainText,
        $maintenance_view: maintenance_view,
        $max_size_in_files: max_size_in_files,
        $maintenance_alert: maintenance_alert,
        $hasInstitutionalCredits: hasInstitutionalCredits,
        $getInstitutionalCreditsFormula: getInstitutionalCreditsFormula,
        $hidesStudyUnitContents: hidesStudyUnitContents,
        $showsStudentsCount: showsStudentsCount,
        $showsStudyUnitRefCompetence: showsStudyUnitRefCompetence,
        $hidesAutonomousWorkHours: hidesAutonomousWorkHours,
        $usesEgressProfileMatterTypes: usesEgressProfileMatterTypes,
        $hidesFormationArea: hidesFormationArea,
        $usesRealizationScopes: usesRealizationScopes,
        $usesAcademicLoad: usesAcademicLoad,
        $showsOnlyNonZeroTimeAllocations: showsOnlyNonZeroTimeAllocations,
        $showsOnlyFilledTimeAllocations: showsOnlyFilledTimeAllocations,
        $logOut: logOut,
    }
});

Vue.config.productionTip = false;


router.beforeEach((to, from, next) => {
    // Para router.onError (vue-router 3 no entrega `to` ahí).
    recordAttemptedRoute(to.fullPath);

    // Silent update: si hay una build más nueva, esta navegación SPA se
    // convierte en una carga completa del documento hacia la MISMA ruta de
    // destino — el usuario ya estaba navegando, así que no pierde estado.
    // oneShotReload trae las guardas (ruta interna, una vez por versión,
    // máximo 2 por pestaña); si no recarga, la navegación sigue normal.
    if (updatePending.value && !UPDATE_SWAP_EXEMPT.includes(to.name)
        && oneShotReload(remoteVersion.value, to.fullPath)) {
        return; // full page load en curso — no llamar next()
    }

    const isAuthenticated = store.getters.isLoged;

    // Si está en mantenimiento, desloguear y permitir solo rutas públicas
    if (maintenance_view) {
        if (isAuthenticated) {
            store.dispatch('logOut');
        }
        if (!public_routes.map(x => x.name).includes(to.name)) {
            return next({ name: 'Login' });
        }
        return next();
    }

    if (from.name == 'DashboardView' && isAuthenticated && store.getters.getUser && !store.getters.getUser.has_changed_password) {
        return
    }
    if (!public_routes.map(x => x.name).includes(to.name) && !isAuthenticated) {
        next({ name: 'Login', query: { redirect: to.fullPath } });
    }
    // Rutas vetadas para un grupo (meta.blockedGroups): se vuelve al inicio
    const user = store.getters.getUser;
    const blockedGroups = to.meta.blockedGroups || [];
    if (isAuthenticated && user && user.group_names && blockedGroups.some(group => user.group_names.includes(group))) {
        return next({ name: 'DashboardView' });
    }
    // if (to.name == 'Login' && isAuthenticated) {
    //     router.replace({ name: 'DashboardView' });
    //     // next();
    //     return
    // }
    next();
    // router.replace('/login', () => {
    //   next(from);
    // })
    // }
});

// Redes de recuperación de chunks: tras un deploy, una pestaña con la build
// vieja puede pedir un chunk que ya no existe (>7 días de retención) — se
// recarga UNA vez hacia la ruta que el usuario intentaba abrir.
// Red 1: fallos de import() a nivel de ruta (lazy routes).
router.onError((error) => {
    if (isChunkLoadError(error)) {
        const target = getAttemptedRoute() || window.location.pathname;
        if (oneShotReload(`chunk:${remoteVersion.value || 'unknown'}`, target)) return;
    }
    console.error(error);
});
// Red 2: imports dinámicos DENTRO de componentes (modales, tabs) que no pasan
// por el router — llegan como promesas rechazadas sin catch.
window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
        const target = window.location.pathname + window.location.search;
        if (oneShotReload(`chunk:${remoteVersion.value || 'unknown'}`, target)) {
            event.preventDefault();
        }
    }
});
// Red 3: errores que Vue captura al renderizar componentes async.
Vue.config.errorHandler = function (err, vm, info) {
    if (isChunkLoadError(err)) {
        const target = window.location.pathname + window.location.search;
        if (oneShotReload(`chunk:${remoteVersion.value || 'unknown'}`, target)) return;
    }
    console.error(err, info);
};

Vue.directive('can', {
    bind: function (el, binding, vnode) {
        try {
            const expressions = binding.expression.split(' ');
            // SEC-3: los permisos ya no se persisten en localStorage — se leen
            // del store (rehidratado antes del mount, ver bootstrap más abajo).
            // Si aún no hay permisos (null), el catch oculta el elemento (fail-closed).
            const permissions = store.state.permissions;
            expressions.forEach(expression => {
                if (!permissions.includes(expression.replace(/'/g, "").replace(/"/g, ""))) {
                    vnode.elm.style.display = "none";
                    return;
                }
            });
        } catch (error) {
            console.log('Directive error: ', error);
            vnode.elm.style.display = "none";
        }
    }
});

function mountApp() {
    new Vue({
        el: '#app',
        router,
        store,
        i18n,
        render: h => h(App),
        mounted: function () {
            const logo_kl = require("@/assets/logo_klearning.png");
            Vue.prototype.$logo = logo_kl;
        }
    });
    startVersionCheck();

    // Ruta preservada al cruzar de klearning.cl a un subdominio de institución
    // (ver buildSubdomainUrl / fetchUserInstitution en store/actions.js). Se lee
    // de window.location y NO de router.currentRoute: tras montar, la navegación
    // inicial de vue-router aún resuelve async, así que currentRoute todavía es
    // START_LOCATION con la query vacía. onReady espera a que esa navegación
    // inicial termine antes de empujar el destino real.
    const redirectQuery = new URLSearchParams(window.location.search).get('redirect');
    if (redirectQuery) {
        router.onReady(() => {
            router.push(redirectQuery).catch(() => { });
        });
    }
}

// SEC-3 fase 2: ya no hay tokens en localStorage — el refresh token vive en una
// cookie HttpOnly y el access solo en memoria. Si el hint no sensible indica que
// probablemente hay cookie de sesión, se hace un silent refresh (restoreSession,
// que encadena fetchUserData) ANTES de montar la app, para que v-can y el guard
// del router vean los permisos en el primer render. Best-effort: si falla (sin
// cookie, vencida o blacklisteada), restoreSession limpia el hint y la app se
// monta sin sesión.
//
// kl_session_redirect (query param): mismo caso, pero llegando desde un
// redirect cross-subdominio (klearning.cl -> {subdomain}.klearning.cl). El
// hint de localStorage no cruza de origin, así que sin esta señal explícita
// la app no intentaría el silent refresh pese a tener cookie de sesión válida.
const sessionRedirectParam = new URLSearchParams(window.location.search).get('kl_session_redirect');
if (localStorage.getItem('kl_session_hint') || sessionRedirectParam) {
    if (sessionRedirectParam) {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('kl_session_redirect');
        window.history.replaceState({}, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
    }
    store.dispatch('restoreSession')
        .catch(() => { })
        .then(mountApp);
} else {
    mountApp();
}
